/** @jsxImportSource @emotion/react */
import { SerializedStyles, css } from "@emotion/react";
import slugify from "@sindresorhus/slugify";
import { FunctionComponent, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";

import {
    StrapiArticleBodyDynamicZone,
    StrapiMarkdownText,
} from "@/types/strapi";

import { Colors } from "@/tokens/color";
import { Spacing } from "@/tokens/spacing";

import { Icon } from "@/ui/atoms/icon";
import { Link } from "@/ui/atoms/link";
import { Text } from "@/ui/atoms/text";

import { useSidebarLinksAnimation } from "@/util/animation_hooks/terminus_sidebar_animations";
import { useArrayRef } from "@/util/hooks/ref_hooks";
import { useTypedTheme } from "@/util/hooks/theme_hooks";

interface TerminusSidebarAnchorsProps {
    article?: StrapiArticleBodyDynamicZone;
    className?: SerializedStyles;
}

export const TerminusSidebarAnchors: FunctionComponent<
    TerminusSidebarAnchorsProps
> = (props) => {
    const _markdownContent = useMemo(() => {
        return (
            props.article
                ?.filter(
                    (article): article is StrapiMarkdownText =>
                        article.__component ===
                        "primitives.primitive-markdown-text",
                )
                .map((article) => article.Text)
                .join("") ?? ""
        );
    }, [props.article]);

    /**
     * Refs
     */
    const buttonRef = useRef<HTMLButtonElement>(null);
    const iconRef = useRef(null);
    const listContentContainerRef = useRef<HTMLUListElement>(null);
    const [listItemsRef, setListItemsRef] = useArrayRef();

    /**
     * State
     */
    const [isExpanded, setIsExpanded] = useState(true);

    /**
     * Hooks
     */

    useSidebarLinksAnimation({
        buttonRef,
        containerRef: listContentContainerRef,
        iconRef,
        isExpanded,
        itemsRef: listItemsRef,
    });

    /**
     * Theme
     */
    const theme = useTypedTheme();

    if (!props.article) {
        return null;
    }

    /**
     * Styles
     */
    const anchorButtonStyles = css({
        alignItems: "center",
        backgroundColor: Colors["transparent"],
        border: "none",
        cursor: "pointer",
        display: "flex",
        gap: Spacing["spacing-2"],
        marginBottom: Spacing["spacing-4"],
        padding: Spacing["spacing-0"],
        position: "relative",
    });

    const anchorsContainerStyles = css({
        paddingInline: Spacing["spacing-2"],
        paddingTop: Spacing["spacing-2"],
    });

    const anchorsListStyles = css({
        display: "flex",
        flexDirection: "column",
        gap: Spacing["spacing-1"],
        overflow: "hidden",
    });

    const linkFlagStyles = css({
        height: Spacing["spacing-3"],
        marginBottom: Spacing["spacing-1"],
        rotate: "90deg",
        width: Spacing["spacing-3"],
    });

    /**
     * Interactivity
     */
    const handleAnchorClick =
        (headingId: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            const anchorElement = document.querySelector(`#${headingId}`);
            if (anchorElement) {
                const elementPosition =
                    anchorElement.getBoundingClientRect().top;

                const offsetPosition =
                    elementPosition +
                    window.pageYOffset -
                    window.innerHeight / 4;

                window.scrollTo({
                    behavior: "smooth",
                    top: offsetPosition,
                });
            }
        };

    return (
        <div css={anchorsContainerStyles}>
            <button
                css={anchorButtonStyles}
                ref={buttonRef}
                onClick={() => setIsExpanded((prev) => !prev)}
            >
                <Text isUppercase fontFamily="mono" fontSize="TEXT_075">
                    Contents
                </Text>

                <Icon
                    className={linkFlagStyles}
                    color={theme.text.textEmphasis}
                    fontSize="TEXT_0625"
                    ref={iconRef}
                    slug="Caret Right"
                />
            </button>

            <ul css={anchorsListStyles} ref={listContentContainerRef}>
                <ReactMarkdown
                    allowedElements={["h2"]}
                    components={{
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        h2: ({ children, node }: any) => {
                            const headingValue = node.children[0].value;
                            const slugifiedHeading = slugify(headingValue);
                            return (
                                <li ref={setListItemsRef}>
                                    <Link
                                        fontSize="TextSmall"
                                        fontWeight="normal"
                                        href={`#${slugifiedHeading}`}
                                        interactionType="hover-color-change"
                                        labelLineHeight={false}
                                        onClick={handleAnchorClick(
                                            slugifiedHeading,
                                        )}
                                    >
                                        {children}
                                    </Link>
                                </li>
                            );
                        },
                    }}
                >
                    {_markdownContent}
                </ReactMarkdown>
            </ul>
        </div>
    );
};
